import Fuse from 'fuse.js';
import type { IFuseOptions } from 'fuse.js';
import type { Endpoint, SearchMatch } from './types';
import { 
  parseQuery, 
  buildSearchExpression, 
  type ParsedQuery,
  type Token,
} from './queryParser';

// Maximum results to return
const MAX_RESULTS = 1000;

// Minimum term length for indexing (shorter terms are too common)
const MIN_TERM_LENGTH = 2;

export interface SearchResult {
  endpoint: Endpoint;
  score: number;
  matches: SearchMatch[];
}

export interface SearchState {
  query: string;
  parsedQuery: ParsedQuery;
  expression: ReturnType<typeof buildSearchExpression>;
}

// Inverted index: token -> Set of endpoint IDs
interface InvertedIndex {
  tokenToIds: Map<string, Set<string>>;
  idToTokens: Map<string, Set<string>>;
}

// Pre-extracted text for each endpoint to avoid recomputation
interface EndpointText {
  general: string;
  input: string;
  output: string;
  all: string;
}

/**
 * Tokenize text into searchable terms
 * Uses search engine-style tokenization:
 * - Lowercase
 * - Split on non-alphanumeric
 * - Remove short terms
 * - Keep original path segments and camelCase/PascalCase splits
 */
function tokenize(text: string): string[] {
  if (!text) return [];
  
  const tokens = new Set<string>();
  const lowerText = text.toLowerCase();
  
  // Split on non-alphanumeric but keep the original parts too
  const parts = lowerText.split(/[^a-z0-9]+/);
  
  for (const part of parts) {
    if (part.length >= MIN_TERM_LENGTH) {
      tokens.add(part);
      
      // Split camelCase and PascalCase
      // e.g., "createUser" -> ["create", "user", "createuser"]
      const camelSplit = part.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase().split(/\s+/);
      for (const camelPart of camelSplit) {
        if (camelPart.length >= MIN_TERM_LENGTH) {
          tokens.add(camelPart);
        }
      }
      
      // Add n-grams for 3+ character terms (prefix matching)
      // e.g., "create" -> "cre", "crea", "creat"
      if (part.length >= 4) {
        for (let i = 3; i < Math.min(part.length, 8); i++) {
          tokens.add(part.substring(0, i));
        }
      }
    }
  }
  
  return Array.from(tokens);
}

/**
 * Extract searchable text from an endpoint (general fields)
 */
function extractGeneralText(endpoint: Endpoint): string {
  const parts: string[] = [
    endpoint.path,
    endpoint.summary || '',
    endpoint.description || '',
    endpoint.operation?.operationId || '',
    ...endpoint.tags,
  ];
  return parts.join(' ');
}

/**
 * Extract original parameter names for exact matching
 */
function extractParameterNames(endpoint: Endpoint): string[] {
  const names: string[] = [];
  if (endpoint.parameters) {
    for (const param of endpoint.parameters) {
      if (param.name) {
        names.push(param.name.toLowerCase());
      }
    }
  }
  return names;
}

/**
 * Extract input-related text (parameters, request body)
 */
function extractInputText(endpoint: Endpoint): string {
  const parts: string[] = [];
  
  if (endpoint.parameters) {
    for (const param of endpoint.parameters) {
      parts.push(param.name);
      parts.push(param.description || '');
      parts.push(param.in || ''); // query, path, header, etc.
    }
  }
  
  if (endpoint.requestBody?.description) {
    parts.push(endpoint.requestBody.description);
  }
  
  // Extract from request body content
  if (endpoint.requestBody?.content) {
    for (const [contentType, content] of Object.entries(endpoint.requestBody.content)) {
      parts.push(contentType);
      if (content.schema) {
        parts.push(...extractSchemaTokens(content.schema));
      }
    }
  }
  
  return parts.join(' ');
}

/**
 * Extract output-related text (responses)
 */
function extractOutputText(endpoint: Endpoint): string {
  const parts: string[] = [];
  
  if (endpoint.responses) {
    for (const [code, response] of Object.entries(endpoint.responses)) {
      parts.push(code);
      if (typeof response === 'object' && response !== null) {
        const resp = response as { 
          description?: string;
          content?: Record<string, { schema?: unknown }>;
        };
        if (resp.description) parts.push(resp.description);
        
        if (resp.content) {
          for (const [contentType, content] of Object.entries(resp.content)) {
            parts.push(contentType);
            if (content?.schema) {
              parts.push(...extractSchemaTokens(content.schema));
            }
          }
        }
      }
    }
  }
  
  return parts.join(' ');
}

/**
 * Extract original property names from schema for exact matching
 */
function extractSchemaPropertyNames(schema: unknown): string[] {
  const names: string[] = [];
  
  if (!schema || typeof schema !== 'object') {
    return names;
  }
  
  const s = schema as Record<string, unknown>;
  
  // Add property names
  if (s.properties && typeof s.properties === 'object') {
    for (const [key, value] of Object.entries(s.properties)) {
      names.push(key.toLowerCase());
      names.push(...extractSchemaPropertyNames(value));
    }
  }
  
  // Extract from items (arrays)
  if (s.items) {
    names.push(...extractSchemaPropertyNames(s.items));
  }
  
  // Handle allOf, oneOf, anyOf
  for (const key of ['allOf', 'oneOf', 'anyOf']) {
    const subSchemas = s[key];
    if (Array.isArray(subSchemas)) {
      for (const subSchema of subSchemas) {
        names.push(...extractSchemaPropertyNames(subSchema));
      }
    }
  }
  
  return names;
}

/**
 * Recursively extract tokens from a schema object
 */
function extractSchemaTokens(schema: unknown): string[] {
  const tokens: string[] = [];
  
  if (!schema || typeof schema !== 'object') {
    return tokens;
  }
  
  const s = schema as Record<string, unknown>;
  
  // Add property names
  if (s.properties && typeof s.properties === 'object') {
    for (const [key, value] of Object.entries(s.properties)) {
      tokens.push(key);
      tokens.push(...extractSchemaTokens(value));
    }
  }
  
  // Add $ref value
  if (s.$ref && typeof s.$ref === 'string') {
    const refParts = s.$ref.split('/');
    tokens.push(refParts[refParts.length - 1] || s.$ref);
  }
  
  // Extract from items (arrays)
  if (s.items) {
    tokens.push(...extractSchemaTokens(s.items));
  }
  
  // Add type and description
  if (s.type && typeof s.type === 'string') {
    tokens.push(s.type);
  }
  if (s.description && typeof s.description === 'string') {
    tokens.push(s.description);
  }
  
  // Add enum values
  if (s.enum && Array.isArray(s.enum)) {
    for (const item of s.enum) {
      if (typeof item === 'string') {
        tokens.push(item);
      }
    }
  }
  
  // Handle allOf, oneOf, anyOf
  for (const key of ['allOf', 'oneOf', 'anyOf']) {
    const subSchemas = s[key];
    if (Array.isArray(subSchemas)) {
      for (const subSchema of subSchemas) {
        tokens.push(...extractSchemaTokens(subSchema));
      }
    }
  }
  
  // Add required field names
  if (s.required && Array.isArray(s.required)) {
    for (const req of s.required) {
      if (typeof req === 'string') {
        tokens.push(req);
      }
    }
  }
  
  // Add additionalProperties if it's an object with schema
  if (s.additionalProperties && typeof s.additionalProperties === 'object') {
    tokens.push(...extractSchemaTokens(s.additionalProperties));
  }
  
  return tokens;
}

/**
 * Build an inverted index from endpoint texts
 */
function buildInvertedIndex(
  endpoints: Endpoint[],
  textExtractor: (e: Endpoint) => string
): InvertedIndex {
  const tokenToIds = new Map<string, Set<string>>();
  const idToTokens = new Map<string, Set<string>>();
  
  for (const endpoint of endpoints) {
    const text = textExtractor(endpoint);
    const tokens = tokenize(text);
    const tokenSet = new Set(tokens);
    
    idToTokens.set(endpoint.id, tokenSet);
    
    for (const token of tokens) {
      let idSet = tokenToIds.get(token);
      if (!idSet) {
        idSet = new Set();
        tokenToIds.set(token, idSet);
      }
      idSet.add(endpoint.id);
    }
  }
  
  return { tokenToIds, idToTokens };
}

/**
 * Build input index with original parameter names preserved
 */
function buildInputIndex(endpoints: Endpoint[]): InvertedIndex {
  const index = buildInvertedIndex(endpoints, extractInputText);
  
  // Add original parameter names for exact matching
  for (const endpoint of endpoints) {
    const paramNames = extractParameterNames(endpoint);
    const tokenSet = index.idToTokens.get(endpoint.id);
    
    if (tokenSet) {
      for (const name of paramNames) {
        // Add to the endpoint's token set
        tokenSet.add(name);
        
        // Add to the global token-to-ids mapping
        let idSet = index.tokenToIds.get(name);
        if (!idSet) {
          idSet = new Set();
          index.tokenToIds.set(name, idSet);
        }
        idSet.add(endpoint.id);
      }
    }
  }
  
  return index;
}

/**
 * Build output index with original schema property names preserved
 */
function buildOutputIndex(endpoints: Endpoint[]): InvertedIndex {
  const index = buildInvertedIndex(endpoints, extractOutputText);
  
  // Add original schema property names for exact matching
  for (const endpoint of endpoints) {
    const propertyNames = new Set<string>();
    
    // Extract property names from all responses
    if (endpoint.responses) {
      for (const response of Object.values(endpoint.responses)) {
        if (typeof response === 'object' && response !== null) {
          const resp = response as { content?: Record<string, { schema?: unknown }> };
          if (resp.content) {
            for (const content of Object.values(resp.content)) {
              if (content?.schema) {
                const names = extractSchemaPropertyNames(content.schema);
                for (const name of names) {
                  propertyNames.add(name);
                }
              }
            }
          }
        }
      }
    }
    
    const tokenSet = index.idToTokens.get(endpoint.id);
    if (tokenSet) {
      for (const name of propertyNames) {
        // Add to the endpoint's token set
        tokenSet.add(name);
        
        // Add to the global token-to-ids mapping
        let idSet = index.tokenToIds.get(name);
        if (!idSet) {
          idSet = new Set();
          index.tokenToIds.set(name, idSet);
        }
        idSet.add(endpoint.id);
      }
    }
  }
  
  return index;
}

// Fuse.js options for fuzzy search (fallback)
const fuseOptions: IFuseOptions<Endpoint> = {
  keys: [
    { name: 'path', weight: 2 },
    { name: 'summary', weight: 1.5 },
    { name: 'description', weight: 1 },
    { name: 'tags', weight: 0.8 },
    { name: 'operation.operationId', weight: 1.2 },
    { name: 'parameters.name', weight: 0.8 },
    { name: 'parameters.description', weight: 0.5 },
  ],
  threshold: 0.3,
  distance: 100,
  includeMatches: true,
  includeScore: true,
  minMatchCharLength: 2,
  shouldSort: true,
  ignoreLocation: true,
  useExtendedSearch: false,
  findAllMatches: false,
  ignoreFieldNorm: true,
};

export class SearchIndex {
  private fuse: Fuse<Endpoint> | null = null;
  private endpoints: Endpoint[] = [];
  private endpointMap = new Map<string, Endpoint>();
  
  // Inverted indices for fast exact/prefix matching
  private generalIndex: InvertedIndex = { tokenToIds: new Map(), idToTokens: new Map() };
  private inputIndex: InvertedIndex = { tokenToIds: new Map(), idToTokens: new Map() };
  private outputIndex: InvertedIndex = { tokenToIds: new Map(), idToTokens: new Map() };
  
  // Pre-extracted texts
  private endpointTexts = new Map<string, EndpointText>();

  buildIndex(endpoints: Endpoint[]): void {
    this.endpoints = endpoints;
    this.endpointMap.clear();
    this.endpointTexts.clear();
    
    // Build endpoint map and pre-extract texts
    for (const endpoint of endpoints) {
      this.endpointMap.set(endpoint.id, endpoint);
      this.endpointTexts.set(endpoint.id, {
        general: extractGeneralText(endpoint),
        input: extractInputText(endpoint),
        output: extractOutputText(endpoint),
        all: [
          extractGeneralText(endpoint),
          extractInputText(endpoint),
          extractOutputText(endpoint),
        ].join(' '),
      });
    }
    
    // Build inverted indices (do this in parallel for speed)
    this.generalIndex = buildInvertedIndex(endpoints, extractGeneralText);
    this.inputIndex = buildInputIndex(endpoints);
    this.outputIndex = buildOutputIndex(endpoints);
    
    // Keep Fuse.js as fallback for fuzzy matching
    this.fuse = new Fuse(endpoints, fuseOptions);
  }

  /**
   * Parse and analyze a query for complex search
   */
  parseQuery(query: string): SearchState {
    const parsedQuery = parseQuery(query);
    const expression = buildSearchExpression(parsedQuery.tokens);
    
    return {
      query,
      parsedQuery,
      expression,
    };
  }

  /**
   * Main search method
   */
  search(query: string): SearchResult[] {
    if (!query.trim()) {
      return this.endpoints.map(e => ({ endpoint: e, score: 1, matches: [] }));
    }

    const searchState = this.parseQuery(query);
    
    // If it's a complex query with operators/filters, use the expression evaluator
    if (searchState.parsedQuery.hasComplexQuery && searchState.expression) {
      return this.searchWithExpression(searchState.expression);
    }

    // Simple search - use inverted index for instant results
    return this.simpleSearch(query);
  }

  /**
   * Simple search using inverted index (instant results)
   */
  private simpleSearch(query: string): SearchResult[] {
    const terms = query.trim().toLowerCase().split(/\s+/).filter(t => t.length >= 2);
    
    if (terms.length === 0) {
      return this.endpoints.map(e => ({ endpoint: e, score: 1, matches: [] }));
    }

    // Use inverted index for exact/prefix matching
    const resultIds = this.findIdsMatchingAllTerms(terms, this.generalIndex);
    
    if (resultIds.size === 0) {
      // Fallback to Fuse.js for fuzzy matching
      return this.fuzzySearchFallback(query);
    }

    // Convert IDs to results with scoring
    const results: SearchResult[] = [];
    for (const id of resultIds) {
      const endpoint = this.endpointMap.get(id);
      if (endpoint) {
        const score = this.calculateScore(endpoint, terms);
        const matches = this.findMatches(endpoint, terms);
        results.push({ endpoint, score, matches });
      }
    }

    // Sort by score
    results.sort((a, b) => a.score - b.score);
    
    return results.slice(0, MAX_RESULTS);
  }

  /**
   * Find endpoint IDs that match ALL terms (AND logic)
   */
  private findIdsMatchingAllTerms(terms: string[], index: InvertedIndex): Set<string> {
    let resultIds: Set<string> | null = null;
    
    for (const term of terms) {
      const tokenSet = index.tokenToIds.get(term);
      if (!tokenSet || tokenSet.size === 0) {
        // Try prefix matching
        const prefixMatches = this.findPrefixMatches(term, index);
        if (prefixMatches.size === 0) {
          return new Set(); // No match for this term
        }
        
        if (resultIds === null) {
          resultIds = new Set(prefixMatches);
        } else {
          resultIds = intersectSets(resultIds, prefixMatches);
        }
      } else {
        if (resultIds === null) {
          resultIds = new Set(tokenSet);
        } else {
          resultIds = intersectSets(resultIds, tokenSet);
        }
      }
      
      if (resultIds.size === 0) {
        return new Set(); // Early termination
      }
    }
    
    return resultIds || new Set();
  }

  /**
   * Find IDs matching any term with given prefix
   */
  private findPrefixMatches(prefix: string, index: InvertedIndex): Set<string> {
    const results = new Set<string>();
    
    // Find all tokens that start with the prefix
    for (const [token, ids] of index.tokenToIds) {
      if (token.startsWith(prefix)) {
        for (const id of ids) {
          results.add(id);
        }
      }
    }
    
    return results;
  }

  /**
   * Find IDs matching ANY term (OR logic)
   */
  private findIdsMatchingAnyTerm(terms: string[], index: InvertedIndex): Set<string> {
    const results = new Set<string>();
    
    for (const term of terms) {
      const tokenSet = index.tokenToIds.get(term);
      if (tokenSet) {
        for (const id of tokenSet) {
          results.add(id);
        }
      } else {
        // Try prefix matching
        const prefixMatches = this.findPrefixMatches(term, index);
        for (const id of prefixMatches) {
          results.add(id);
        }
      }
    }
    
    return results;
  }

  /**
   * Fuzzy search fallback using Fuse.js
   */
  private fuzzySearchFallback(query: string): SearchResult[] {
    if (!this.fuse) return [];
    
    const results = this.fuse.search(query, { limit: MAX_RESULTS });
    return results.map((r) => ({
      endpoint: r.item,
      score: r.score ?? 1,
      matches: this.convertFuseMatches(r.matches),
    }));
  }

  /**
   * Extract all searchable terms from an expression for highlighting
   */
  private extractTermsFromExpression(expression: SearchExpression | null | undefined): string[] {
    if (!expression) return [];
    
    const terms: string[] = [];
    
    switch (expression.type) {
      case 'and':
      case 'or': {
        // Recursively get terms from both sides
        if (expression.left) {
          terms.push(...this.extractTermsFromExpression(expression.left));
        }
        if (expression.right) {
          terms.push(...this.extractTermsFromExpression(expression.right));
        }
        break;
      }
      case 'in':
      case 'out':
      case 'exact':
      case 'term': {
        // These types have a value to search for
        if (expression.value) {
          terms.push(expression.value);
        }
        break;
      }
    }
    
    return terms;
  }

  /**
   * Search using a parsed expression tree
   */
  private searchWithExpression(expression: NonNullable<SearchState['expression']>): SearchResult[] {
    const results: SearchResult[] = [];
    
    // Evaluate expression to get matching IDs first (fast)
    const matchingIds = this.evaluateExpressionToIds(expression);
    
    if (matchingIds.size === 0) {
      return [];
    }
    
    // Extract all terms for highlighting
    const terms = this.extractTermsFromExpression(expression);
    
    // Then get full results with matches
    for (const id of matchingIds) {
      const endpoint = this.endpointMap.get(id);
      if (endpoint) {
        const score = this.calculateScore(endpoint, terms.map(t => t.toLowerCase()));
        const matches = this.findMatches(endpoint, terms.map(t => t.toLowerCase()));
        results.push({ endpoint, score, matches });
      }
    }

    results.sort((a, b) => a.score - b.score);
    return results.slice(0, MAX_RESULTS);
  }

  /**
   * Evaluate expression to get matching endpoint IDs (FAST - uses inverted index)
   */
  private evaluateExpressionToIds(
    expression: NonNullable<SearchState['expression']> | null
  ): Set<string> {
    if (!expression) {
      return new Set(this.endpoints.map(e => e.id));
    }

    switch (expression.type) {
      case 'and': {
        if (!expression.left || !expression.right) {
          return new Set();
        }
        const left = this.evaluateExpressionToIds(expression.left);
        const right = this.evaluateExpressionToIds(expression.right);
        return intersectSets(left, right);
      }
      
      case 'or': {
        if (!expression.left || !expression.right) {
          return new Set();
        }
        const left = this.evaluateExpressionToIds(expression.left);
        const right = this.evaluateExpressionToIds(expression.right);
        return unionSets(left, right);
      }
      
      case 'in': {
        const term = expression.value?.toLowerCase() || '';
        const ids = this.inputIndex.tokenToIds.get(term);
        if (ids) return new Set(ids);
        return this.findPrefixMatches(term, this.inputIndex);
      }
      
      case 'out': {
        const term = expression.value?.toLowerCase() || '';
        const ids = this.outputIndex.tokenToIds.get(term);
        if (ids) return new Set(ids);
        return this.findPrefixMatches(term, this.outputIndex);
      }
      
      case 'exact': {
        const term = expression.value?.toLowerCase() || '';
        return this.findExactMatches(term);
      }
      
      case 'term': {
        const term = expression.value?.toLowerCase() || '';
        const ids = this.generalIndex.tokenToIds.get(term);
        if (ids) return new Set(ids);
        return this.findPrefixMatches(term, this.generalIndex);
      }
      
      default:
        return new Set();
    }
  }

  /**
   * Find exact substring matches
   */
  private findExactMatches(term: string): Set<string> {
    const results = new Set<string>();
    
    for (const [id, texts] of this.endpointTexts) {
      if (texts.all.toLowerCase().includes(term)) {
        results.add(id);
      }
    }
    
    return results;
  }

  /**
   * Calculate relevance score for an endpoint
   */
  private calculateScore(endpoint: Endpoint, terms: string[]): number {
    const texts = this.endpointTexts.get(endpoint.id);
    if (!texts) return 1;
    
    const text = texts.all.toLowerCase();
    let score = 0;
    
    for (const term of terms) {
      // Exact match in path gets highest score
      if (endpoint.path.toLowerCase().includes(term)) {
        score -= 0.3;
      }
      // Exact match in operationId is also good
      if (endpoint.operation?.operationId?.toLowerCase().includes(term)) {
        score -= 0.25;
      }
      // Match in summary
      if (endpoint.summary?.toLowerCase().includes(term)) {
        score -= 0.2;
      }
      // General match
      if (text.includes(term)) {
        score -= 0.1;
      }
    }
    
    return Math.max(0, score + 1);
  }

  /**
   * Find match locations for highlighting in a specific text
   */
  private findMatchesInText(text: string, term: string): [number, number][] {
    const indices: [number, number][] = [];
    const lowerText = text.toLowerCase();
    let pos = 0;
    
    while ((pos = lowerText.indexOf(term, pos)) !== -1) {
      indices.push([pos, pos + term.length - 1]);
      pos += term.length;
    }
    
    return indices;
  }

  /**
   * Find match locations for highlighting
   * Searches in specific fields to provide field-specific match keys
   */
  private findMatches(endpoint: Endpoint, terms: string[]): SearchMatch[] {
    const matches: SearchMatch[] = [];
    const texts = this.endpointTexts.get(endpoint.id);
    if (!texts) return matches;
    
    for (const term of terms) {
      // Search in path
      const pathIndices = this.findMatchesInText(endpoint.path, term);
      if (pathIndices.length > 0) {
        matches.push({
          key: 'path',
          value: endpoint.path,
          indices: pathIndices,
        });
      }
      
      // Search in summary
      if (endpoint.summary) {
        const summaryIndices = this.findMatchesInText(endpoint.summary, term);
        if (summaryIndices.length > 0) {
          matches.push({
            key: 'summary',
            value: endpoint.summary,
            indices: summaryIndices,
          });
        }
      }
      
      // Search in description
      if (endpoint.description) {
        const descIndices = this.findMatchesInText(endpoint.description, term);
        if (descIndices.length > 0) {
          matches.push({
            key: 'description',
            value: endpoint.description,
            indices: descIndices,
          });
        }
      }
      
      // Search in operationId
      if (endpoint.operation?.operationId) {
        const opIdIndices = this.findMatchesInText(endpoint.operation.operationId, term);
        if (opIdIndices.length > 0) {
          matches.push({
            key: 'operation.operationId',
            value: endpoint.operation.operationId,
            indices: opIdIndices,
          });
        }
      }
      
      // Search in parameters
      if (endpoint.parameters) {
        for (const param of endpoint.parameters) {
          // Search in parameter name
          if (param.name) {
            const nameIndices = this.findMatchesInText(param.name, term);
            if (nameIndices.length > 0) {
              matches.push({
                key: 'parameters.name',
                value: param.name,
                indices: nameIndices,
              });
            }
          }
          
          // Search in parameter description
          if (param.description) {
            const descIndices = this.findMatchesInText(param.description, term);
            if (descIndices.length > 0) {
              matches.push({
                key: 'parameters.description',
                value: param.description,
                indices: descIndices,
              });
            }
          }
        }
      }
      
      // Search in request body content (if present)
      if (endpoint.requestBody?.content) {
        for (const [contentType, content] of Object.entries(endpoint.requestBody.content)) {
          if (contentType.toLowerCase().includes(term)) {
            matches.push({
              key: 'requestBody.contentType',
              value: contentType,
              indices: [[0, term.length - 1]],
            });
          }
        }
      }
      
      // Search in responses
      if (endpoint.responses) {
        for (const [code, response] of Object.entries(endpoint.responses)) {
          if (typeof response === 'object' && response !== null) {
            const resp = response as { description?: string; content?: Record<string, unknown> };
            
            // Search in response code
            if (code.toLowerCase().includes(term)) {
              matches.push({
                key: 'responses.code',
                value: code,
                indices: [[0, term.length - 1]],
              });
            }
            
            // Search in response description
            if (resp.description) {
              const descIndices = this.findMatchesInText(resp.description, term);
              if (descIndices.length > 0) {
                matches.push({
                  key: 'responses.description',
                  value: resp.description,
                  indices: descIndices,
                });
              }
            }
            
            // Search in response content types
            if (resp.content) {
              for (const contentType of Object.keys(resp.content)) {
                if (contentType.toLowerCase().includes(term)) {
                  matches.push({
                    key: 'responses.contentType',
                    value: contentType,
                    indices: [[0, term.length - 1]],
                  });
                }
              }
            }
          }
        }
      }
    }
    
    return matches;
  }

  private convertFuseMatches(matches: ReadonlyArray<{ key?: string; value?: string; indices: ReadonlyArray<[number, number]> }> | undefined): SearchMatch[] {
    if (!matches) return [];
    return matches.map(m => ({
      key: m.key ?? '',
      value: m.value ?? '',
      indices: Array.from(m.indices).map(idx => [idx[0], idx[1]] as [number, number]),
    }));
  }

  // Advanced search with filters
  searchWithFilters(
    query: string,
    filters: {
      methods?: string[];
      specs?: string[];
      tags?: string[];
    }
  ): SearchResult[] {
    let results = this.search(query);

    // Apply method filter
    if (filters.methods && filters.methods.length > 0) {
      results = results.filter((r) => filters.methods!.includes(r.endpoint.method));
    }

    // Apply spec filter
    if (filters.specs && filters.specs.length > 0) {
      results = results.filter((r) => filters.specs!.includes(r.endpoint.specId));
    }

    // Apply tag filter
    if (filters.tags && filters.tags.length > 0) {
      results = results.filter((r) =>
        r.endpoint.tags.some((tag) => filters.tags!.includes(tag))
      );
    }

    return results;
  }

  // Get suggestions for autocomplete - DISABLED for better UX
  getSuggestions(_query: string, _limit = 5): string[] {
    return [];
  }

  // Get all unique tags across all endpoints
  getAllTags(): string[] {
    const tags = new Set<string>();
    for (const endpoint of this.endpoints) {
      for (const tag of endpoint.tags) {
        tags.add(tag);
      }
    }
    return Array.from(tags).sort();
  }

  // Get all unique specs
  getAllSpecs(): { id: string; title: string }[] {
    const specs = new Map<string, string>();
    for (const endpoint of this.endpoints) {
      if (!specs.has(endpoint.specId)) {
        specs.set(endpoint.specId, endpoint.specTitle || 'Unknown');
      }
    }
    return Array.from(specs.entries()).map(([id, title]) => ({ id, title }));
  }

  // Clear the index
  clear(): void {
    this.fuse = null;
    this.endpoints = [];
    this.endpointMap.clear();
    this.endpointTexts.clear();
    this.generalIndex = { tokenToIds: new Map(), idToTokens: new Map() };
    this.inputIndex = { tokenToIds: new Map(), idToTokens: new Map() };
    this.outputIndex = { tokenToIds: new Map(), idToTokens: new Map() };
  }
}

// Set operations
function intersectSets(a: Set<string>, b: Set<string>): Set<string> {
  const result = new Set<string>();
  // Iterate over the smaller set for efficiency
  if (a.size > b.size) {
    for (const item of b) {
      if (a.has(item)) result.add(item);
    }
  } else {
    for (const item of a) {
      if (b.has(item)) result.add(item);
    }
  }
  return result;
}

function unionSets(a: Set<string>, b: Set<string>): Set<string> {
  const result = new Set(a);
  for (const item of b) {
    result.add(item);
  }
  return result;
}

// Singleton instance
export const searchIndex = new SearchIndex();
